// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

import backend from "../backend";
import NewEmailMessageAttachment from "../../model/NewEmailMessageAttachment";
import { HttpResult } from "../http";
import NewEmailMessage from "../../model/NewEmailMessage";
import BusinessPreview from "../../model/BusinessPreview";
import AccountPreview from "../../model/AccountPreview";
import LeadPreview from "../../model/LeadPreview";

const BASE_URI = "/emailmessages";
const BASE_URI_DETAILS = "/emailmessagedetails/";
const BASE_URI_ATTACHMENT = "/emailmessageattachment/";

export default {
  /**
   * Posts email message to the backend server (for association)
   * @param {string} emailMessageId - id of the email message
   * @param {NewEmailMessageAttachment} emailMessageAttachment - email message attachment
   * @returns {Promise} Request promise
   */
  postEmailMessageAttachment(emailMessageId: string, emailMessageAttachment: NewEmailMessageAttachment) {
    return backend.post(BASE_URI_DETAILS + emailMessageId + "/attachment/", emailMessageAttachment);
  },

  /**
   * Saves the email attachment file on the backend server
   * @param {string} attachmentId - id of the associated attachment
   * @param {Blob} file - Attachment file
   * @returns {Promise} Async promise
   */
  postEmailMessageAttachmentFile(attachmentId: string, file: Blob) {
    return backend.post(BASE_URI_ATTACHMENT + attachmentId + "/file/", file, {
      headers: {
        "Content-Type": file.type,
      },
    });
  },

  /**
   * Gets associated contacts identifier from backend server
   * @param {string} messageId - email message id
   * @returns {Promise} List of contacts identifiers
   */
  getAssociatedContactsId(messageId: string): Promise<HttpResult<string[]>> {
    const url = BASE_URI + "?providerEmailId=" + encodeURIComponent(messageId);
    return backend.get(url);
  },

  /**
   * Gets associated businesses preview from backend server
   * @param {string} messageId - email message id
   * @returns {Promise} List of business
   */
  getAssociatedBusinesses(messageId: string): Promise<HttpResult<BusinessPreview[]>> {
    const url = BASE_URI + "/business?providerEmailId=" + encodeURIComponent(messageId);
    return backend.get(url);
  },

  /**
   * Posts email message to the backend server (for association)
   * @param {NewEmailMessage} emailMessage - email message
   * @returns {Promise} Request promise
   */
  postEmailMessage(emailMessage: NewEmailMessage) {
    return backend.post(BASE_URI, emailMessage);
  },

  /**
   * Gets associated accounts preview from backend server
   * @param {string} messageId - email message id
   * @returns {Promise} List of accounts
   */
  getAssociatedAccounts(messageId: string): Promise<HttpResult<AccountPreview[]>> {
    const url = BASE_URI + "/account?providerEmailId=" + encodeURIComponent(messageId);
    return backend.get(url);
  },

  /**
   * Gets associated leads preview from backend server
   * @param {string} messageId - email message id
   * @returns {Promise} List of leads
   */
  getAssociatedLeads(messageId: string): Promise<HttpResult<LeadPreview[]>> {
    const url = BASE_URI + "/lead?providerEmailId=" + encodeURIComponent(messageId);
    return backend.get(url);
  },
};
